import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AuthenticationService, ItemToSellPicture, ItemToSellSource, ItemsToSellService } from 'src/app/_services';
import { PictureRecommendation, PictureService } from 'src/app/_services/picture.service';
import { Profile, ProfileService } from 'src/app/_services/profiles.service';
import { MaterialPage } from 'src/app/components/page/material-page.component';
import { Angulartics2 } from 'angulartics2';
import { SidenavService } from 'src/app/_services/sidenav.service';
import { ViewportScroller } from '@angular/common';
import { ImageListEditorComponent } from 'src/app/modules/shared/image-list-editor/components/image-list-editor/image-list-editor.component';

@Component({
  selector: 'snap-and-sell',
  templateUrl: './snap-and-sell-page.component.html',
  styleUrls: ['./snap-and-sell-page.component.scss']
})
export class SnapAndSellPageComponent extends MaterialPage implements OnInit {

  @ViewChild('stepper') stepper: MatStepper;

  firstPicture: ItemToSellPicture[] = [];
  recommendationPictures = new Map<PictureRecommendation, ItemToSellPicture[]>;
  loggedInUserProfile: Profile | undefined;
  
  loadingRecommendations: boolean = false;
  recommendations: PictureRecommendation[] = [];
  itemType?: string;
  
  loading: boolean = false;
  picturesUploading: boolean = false;
  
  firstPictureForm = new FormGroup({
    picturesLength: new FormControl(0, [Validators.min(1)])
  });
  
  manualForm = new FormGroup({
    condition: new FormControl('', [Validators.required]),
  });
  
  stepperIconMap = new Map<number, string>([
    [0, 'add_a_photo'],
    [1, 'add_a_photo'],
    [2, 'edit_note']
  ]);
  
  @ViewChildren(ImageListEditorComponent)
  public imageListEditors: QueryList<ImageListEditorComponent>;
  
  constructor(
    private itemsToSellService: ItemsToSellService,
    private router: Router,
    breakpointObserver: BreakpointObserver,
    private pictureService: PictureService,
    private authenticationService: AuthenticationService,
    private profileService: ProfileService,
    titleService: Title,
    meta: Meta,
    private angulartics: Angulartics2,
    sidenavService: SidenavService,
    private viewportScroller: ViewportScroller
  ) {
    super(breakpointObserver, titleService, meta, sidenavService, router, $localize `:page title:Snap&Sell`, '', true);
  }
  
  async ngOnInit() {
    
    if (this.authenticationService.isAuthenticated()) {
      this.loggedInUserProfile = (await this.profileService.list())[0];
    }  
  }
  
  async askForRecommendations() {
    
    if (this.firstPictureForm.invalid) {
      this.firstPictureForm.markAllAsTouched();
      console.log("Invalid form");
      return;
    }
    
    this.loadingRecommendations = true;
    const recommendations = await this.pictureService.getPictureRecommendations(this.firstPicture[0].key);
    this.initRecommendationPictures(recommendations ? recommendations.recommendations : []);
    this.itemType = recommendations?.itemType;
    this.recommendations = recommendations ? recommendations.recommendations : [];
    this.stepper.next();
    
    this.angulartics.eventTrack.next({
      action: "Got recommendations"
    });
    
    this.loadingRecommendations = false;
  }
  
  prev() {
    this.stepper.previous();
  }
  
  onStepChange(event: any) {
    this.viewportScroller.scrollToPosition([0, 0]);
  }
  
  async createListing() {
    
    if (this.manualForm.invalid) {
      console.log("Invalid form");
      return;
    }
    
    this.loading = true;
    let createdItem;
    this.manualForm.disable();
    
    const pictures = [this.firstPicture[0], ...this.collectAllRecommendationPictures()];
      
    try {
      createdItem = await this.itemsToSellService.create({
        condition: +this.manualForm.controls["condition"].value!,
        pictures: pictures,
        persist: false,
        source: ItemToSellSource.SNAP_AND_SELL
      });

      if (!this.loggedInUserProfile) {
        this.profileService.persistLocally(new Profile(createdItem.location));
      }
      
      this.itemsToSellService.persistLocally(createdItem);
    } finally {
      this.loading = false;
      this.manualForm.enable();
    }
    
    this.router.navigate(["/view-listing/local"], { state: { flow: '/snap-and-sell' }});
  }
  
  private collectAllRecommendationPictures(): ItemToSellPicture[] {
    
    let pictures: ItemToSellPicture[] = [];
    
    for (let recommendationPictures of this.recommendationPictures.values()) {
      recommendationPictures.forEach(picture => {
        pictures.push(new ItemToSellPicture(picture.key, pictures.length + 1));
      })
    }
    
    return pictures;
  }
  
  public numberOfPicturesOnFirstStepChanged() {
    this.firstPictureForm.controls["picturesLength"].setValue(this.firstPicture.length);
  }
  
  private initRecommendationPictures(recommendations: PictureRecommendation[]) {
    
    this.recommendationPictures.clear();
    
    recommendations.forEach(recommendation => {
      this.recommendationPictures.set(recommendation, []);
    })
  }
  
  getStepIcon(stepIndex: number): string {
    return this.stepperIconMap.get(stepIndex)!;
  }
  
  getRecommendationPictures(recommendation: PictureRecommendation): ItemToSellPicture[] {
    return this.recommendationPictures.get(recommendation)!;
  }
  
  onPicturesUploadingStateChange() {
    this.picturesUploading = !!this.imageListEditors.find(editor => editor.uploading === true);
  }
}
